import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, TestTube, Save, Trash2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface WhatsAppConfigFormProps {
  onConfigurationSaved?: () => void;
  onConfigurationRemoved?: () => void;
}

export function WhatsAppConfigForm({ 
  onConfigurationSaved, 
  onConfigurationRemoved 
}: WhatsAppConfigFormProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [config, setConfig] = useState({
    apiKey: '',
    baseUrl: 'https://simplesign.f2dev.com.br/api',
    testPhone: '',
    dailyLimit: 100,
    isActive: false
  });

  const [testResults, setTestResults] = useState<{
    connection?: { success: boolean; message: string };
    message?: { success: boolean; message: string };
  }>({});

  const { toast } = useToast();

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      // Usar API simulada para desenvolvimento com Vite
      const { mockGetConfig } = await import('@/utils/mockWhatsAppAPI');
      const data = await mockGetConfig();
      
      if (data.success && data.data && data.data.isConfigured) {
        const existingConfig = data.data;
        setConfig(prev => ({
          ...prev,
          isActive: existingConfig.isActive,
          testPhone: existingConfig.testPhone || '',
          dailyLimit: existingConfig.dailyLimit || 100
        }));
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar a configuração.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async () => {
    if (!config.apiKey) {
      toast({
        title: "Erro",
        description: "Digite a API key antes de testar a conexão.",
        variant: "destructive"
      });
      return;
    }

    setIsTesting(true);
    setTestResults(prev => ({ ...prev, connection: undefined }));

    try {
      // Usar API simulada para desenvolvimento com Vite
      const { mockTestConnection } = await import('@/utils/mockWhatsAppAPI');
      const data = await mockTestConnection({
        apiKey: config.apiKey,
        baseUrl: config.baseUrl
      });
      
      setTestResults(prev => ({
        ...prev,
        connection: {
          success: data.success,
          message: data.message
        }
      }));

      toast({
        title: data.success ? "Sucesso" : "Erro",
        description: data.message,
        variant: data.success ? "default" : "destructive"
      });
    } catch (error) {
      const message = "Erro ao testar conexão";
      setTestResults(prev => ({
        ...prev,
        connection: {
          success: false,
          message
        }
      }));
      toast({
        title: "Erro",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsTesting(false);
    }
  };

  const sendTestMessage = async () => {
    if (!config.apiKey || !config.testPhone) {
      toast({
        title: "Erro",
        description: "Configure a API key e um número de teste.",
        variant: "destructive"
      });
      return;
    }

    setIsSendingTest(true);
    setTestResults(prev => ({ ...prev, message: undefined }));

    try {
      // Usar API simulada para desenvolvimento com Vite
      const { mockSendTestMessage } = await import('@/utils/mockWhatsAppAPI');
      const data = await mockSendTestMessage(config.testPhone, {
        apiKey: config.apiKey,
        baseUrl: config.baseUrl
      });
      
      setTestResults(prev => ({
        ...prev,
        message: {
          success: data.success,
          message: data.message
        }
      }));

      toast({
        title: data.success ? "Sucesso" : "Erro",
        description: data.message,
        variant: data.success ? "default" : "destructive"
      });
    } catch (error) {
      const message = "Erro ao enviar mensagem de teste";
      setTestResults(prev => ({
        ...prev,
        message: {
          success: false,
          message
        }
      }));
      toast({
        title: "Erro",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const saveConfiguration = async () => {
    if (!config.apiKey) {
      toast({
        title: "Erro",
        description: "A API key é obrigatória.",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);

    try {
      // Usar API simulada para desenvolvimento com Vite
      const { mockSaveConfig } = await import('@/utils/mockWhatsAppAPI');
      const data = await mockSaveConfig({
        apiKey: config.apiKey,
        baseUrl: config.baseUrl,
        testPhone: config.testPhone,
        dailyLimit: config.dailyLimit,
        isActive: config.isActive
      });

      if (data.success) {
        toast({
          title: "Sucesso",
          description: "Configuração salva com sucesso!",
        });
        onConfigurationSaved?.();
      } else {
        toast({
          title: "Erro",
          description: data.message || "Erro ao salvar configuração.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar configuração.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const removeConfiguration = async () => {
    if (!confirm('Tem certeza que deseja remover a configuração da API WhatsApp?')) {
      return;
    }

    setIsDeleting(true);

    try {
      // Usar API simulada para desenvolvimento com Vite
      const { mockDeleteConfig } = await import('@/utils/mockWhatsAppAPI');
      const data = await mockDeleteConfig();

      if (data.success) {
        toast({
          title: "Sucesso",
          description: "Configuração removida com sucesso!",
        });
        setConfig({
          apiKey: '',
          baseUrl: 'https://simplesign.f2dev.com.br/api',
          testPhone: '',
          dailyLimit: 100,
          isActive: false
        });
        setTestResults({});
        onConfigurationRemoved?.();
      } else {
        toast({
          title: "Erro",
          description: data.error || "Erro ao remover configuração.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao remover configuração.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Configuração da API</CardTitle>
            <CardDescription>
              Configure as credenciais da API SimpleSign para envio de mensagens WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key *</Label>
              <Input
                id="apiKey"
                type="password"
                value={config.apiKey}
                onChange={(e) => setConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                placeholder="black-white-bot-secure-key-2025"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="baseUrl">URL Base da API</Label>
              <Input
                id="baseUrl"
                value={config.baseUrl}
                onChange={(e) => setConfig(prev => ({ ...prev, baseUrl: e.target.value }))}
                placeholder="https://simplesign.f2dev.com.br/api"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="testPhone">Telefone para Teste</Label>
              <Input
                id="testPhone"
                value={config.testPhone}
                onChange={(e) => setConfig(prev => ({ ...prev, testPhone: e.target.value }))}
                placeholder="(11) 99999-9999"
              />
              <p className="text-sm text-muted-foreground">
                Formato: (DD) XXXXX-XXXX ou DDDXXXXXXXXX
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dailyLimit">Limite Diário de Mensagens</Label>
              <Input
                id="dailyLimit"
                type="number"
                min="1"
                max="1000"
                value={config.dailyLimit}
                onChange={(e) => setConfig(prev => ({ ...prev, dailyLimit: parseInt(e.target.value) || 100 }))}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={config.isActive}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, isActive: checked }))}
              />
              <Label htmlFor="isActive">Ativar envio de mensagens</Label>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={saveConfiguration} 
                disabled={isSaving}
                className="flex-1"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Salvar Configuração
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={removeConfiguration}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Testes de Configuração</CardTitle>
            <CardDescription>
              Verifique se a conexão com a API está funcionando corretamente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">Testar Conexão</Label>
                <p className="text-sm text-foreground mb-2">
                  Verifica se as credenciais da API estão corretas
                </p>
                <Button 
                  onClick={testConnection} 
                  disabled={isTesting || !config.apiKey}
                  variant="outline"
                  className="w-full"
                >
                  {isTesting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testando...
                    </>
                  ) : (
                    <>
                      <TestTube className="h-4 w-4 mr-2" />
                      Testar Conexão
                    </>
                  )}
                </Button>

                {testResults.connection && (
                  <Alert className={testResults.connection.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                    <div className="flex items-center">
                      {testResults.connection.success ? (
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600 mr-2" />
                      )}
                      <AlertDescription className={testResults.connection.success ? "text-green-800" : "text-red-800"}>
                        {testResults.connection.message}
                      </AlertDescription>
                    </div>
                  </Alert>
                )}
              </div>

              <div>
                <Label className="text-base font-medium">Enviar Mensagem de Teste</Label>
                <p className="text-sm text-foreground mb-2">
                  Envia uma mensagem de teste para o número configurado
                </p>
                <Button 
                  onClick={sendTestMessage} 
                  disabled={isSendingTest || !config.apiKey || !config.testPhone}
                  variant="outline"
                  className="w-full"
                >
                  {isSendingTest ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <TestTube className="h-4 w-4 mr-2" />
                      Enviar Teste
                    </>
                  )}
                </Button>

                {testResults.message && (
                  <Alert className={testResults.message.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                    <div className="flex items-center">
                      {testResults.message.success ? (
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600 mr-2" />
                      )}
                      <AlertDescription className={testResults.message.success ? "text-green-800" : "text-red-800"}>
                        {testResults.message.message}
                      </AlertDescription>
                    </div>
                  </Alert>
                )}
              </div>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Dica:</strong> Teste sempre a configuração antes de ativar o envio de mensagens.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
